import { createFileRoute, Link } from '@tanstack/react-router';
import {
    Activity,
    AlertTriangle,
    ArrowLeft,
    ArrowUpRight,
    BarChart3,
    CheckCircle2,
    Gauge,
    Search,
    ShieldAlert,
} from 'lucide-react';
import { type ReactNode, useMemo, useState } from 'react';

import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { cn } from '@apps/web/lib/utils';
import { useAdminAgentToolTelemetry } from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type ApiResponseType,
    type Mode,
} from '@libraries/typescript/api_client';

type ToolTelemetryResponse = ApiResponseType<
    '/admin/agent/tool-telemetry',
    'get'
>;
type OutputVolumeRow = ToolTelemetryResponse['output_volume_by_tool'][number];
type FailureTaxonomyRow =
    ToolTelemetryResponse['failure_taxonomy_by_tool'][number];
type DrillDownRateRow = ToolTelemetryResponse['drill_down_rates'][number];
type TrendBucketRow = ToolTelemetryResponse['call_volume_trends'][number];
type RunExampleRow = ToolTelemetryResponse['largest_output_runs'][number];

const WINDOW_OPTIONS = [7, 14, 30, 60, 90] as const;
type WindowOption = (typeof WINDOW_OPTIONS)[number];
type ModeFilter = 'all' | Mode;

const NUMBER_FORMATTER = new Intl.NumberFormat();
const RATE_FORMATTER = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: 1,
    style: 'percent',
});
const DAY_FORMATTER = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
});
const DATE_TIME_FORMATTER = new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
});

export const Route = createFileRoute('/_app/admin/agent/tool-telemetry')({
    head: () => ({
        meta: [
            {
                title: 'Tool Telemetry | Phoebe Admin',
            },
        ],
    }),
    component: AdminAgentToolTelemetryPage,
});

export function AdminAgentToolTelemetryPage() {
    const [windowDays, setWindowDays] = useState<WindowOption>(30);
    const [modeFilter, setModeFilter] = useState<ModeFilter>('all');
    const queryParams = useMemo(
        () => ({
            window_days: windowDays,
            mode: modeFilter === 'all' ? undefined : modeFilter,
        }),
        [modeFilter, windowDays],
    );
    const { data, error, isLoading, isValidating } =
        useAdminAgentToolTelemetry(queryParams);
    const trendRows = useMemo(
        () => buildTrendRows(data?.call_volume_trends ?? []),
        [data?.call_volume_trends],
    );

    return (
        <div className="bg-background flex h-[calc(100svh-3.5rem)] max-h-[calc(100svh-3.5rem)] min-h-0 w-full flex-col overflow-hidden md:h-svh md:max-h-svh">
            <header className="shrink-0 border-b bg-white px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                            <Button
                                asChild={true}
                                type="button"
                                size="icon"
                                variant="ghost"
                                className="size-8 shrink-0 text-[#666666] hover:bg-[#f5f5f5] hover:text-[#171717]"
                            >
                                <Link to="/admin/agent">
                                    <ArrowLeft className="size-4" />
                                </Link>
                            </Button>
                            <Gauge className="size-5 shrink-0 text-[#171717]" />
                            <h1 className="truncate text-xl font-semibold text-[#171717]">
                                Tool Telemetry
                            </h1>
                            {isValidating ? (
                                <Badge
                                    variant="outline"
                                    className="text-[11px] text-[#666666]"
                                >
                                    Refreshing
                                </Badge>
                            ) : null}
                        </div>
                        {data ? (
                            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#666666]">
                                <span>
                                    {formatDateTime(data.window.started_at)} to{' '}
                                    {formatDateTime(data.window.ended_at)}
                                </span>
                                <span className="text-[#d4d4d4]">/</span>
                                <span>
                                    {formatNumber(data.summary.run_count)} runs
                                </span>
                                <span className="text-[#d4d4d4]">/</span>
                                <span>
                                    {formatNumber(data.summary.tool_count)}{' '}
                                    tools
                                </span>
                            </div>
                        ) : null}
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Select
                            value={String(windowDays)}
                            onValueChange={(value) => {
                                setWindowDays(Number(value) as WindowOption);
                            }}
                        >
                            <SelectTrigger className="h-8 w-[126px] text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {WINDOW_OPTIONS.map((option) => (
                                    <SelectItem
                                        key={option}
                                        value={String(option)}
                                    >
                                        {option} days
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select
                            value={modeFilter}
                            onValueChange={(value) => {
                                setModeFilter(value as ModeFilter);
                            }}
                        >
                            <SelectTrigger className="h-8 w-[128px] text-xs">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All modes</SelectItem>
                                <SelectItem value="live">Live</SelectItem>
                                <SelectItem value="sandbox">Sandbox</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </div>
            </header>
            <main className="min-h-0 flex-1 overflow-y-auto bg-[#fafafa] p-4">
                {isLoading ? (
                    <StatePanel label="Loading tool telemetry" />
                ) : error ? (
                    <StatePanel
                        label="Tool telemetry failed to load"
                        tone="danger"
                    />
                ) : data ? (
                    <div className="mx-auto flex max-w-[1500px] flex-col gap-4">
                        <SummaryGrid summary={data.summary} />
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(360px,0.8fr)]">
                            <OutputVolumePanel
                                rows={data.output_volume_by_tool}
                            />
                            <FailureTaxonomyPanel
                                rows={data.failure_taxonomy_by_tool}
                            />
                        </div>
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
                            <DrillDownPanel rows={data.drill_down_rates} />
                            <TrendPanel rows={trendRows} />
                        </div>
                        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                            <RunExamplesPanel
                                title="Largest Outputs"
                                rows={data.largest_output_runs}
                                metric="visible"
                            />
                            <RunExamplesPanel
                                title="Recent Failures"
                                rows={data.recent_failure_runs}
                                metric="failure"
                            />
                        </div>
                    </div>
                ) : (
                    <StatePanel label="No telemetry available" />
                )}
            </main>
        </div>
    );
}

function SummaryGrid({
    summary,
}: {
    readonly summary: ToolTelemetryResponse['summary'];
}) {
    const failureCount = summary.failed_call_count + summary.denied_call_count;
    return (
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <SummaryMetric
                label="Calls"
                value={summary.total_call_count}
                icon={<Activity className="size-4" />}
            />
            <SummaryMetric
                label="Success"
                value={summary.successful_call_count}
                icon={<CheckCircle2 className="size-4" />}
                tone="success"
            />
            <SummaryMetric
                label="Failures"
                value={failureCount}
                icon={<ShieldAlert className="size-4" />}
                tone={failureCount > 0 ? 'danger' : 'neutral'}
            />
            <SummaryMetric
                label="Budget Denied"
                value={summary.budget_denied_call_count}
                icon={<Gauge className="size-4" />}
                tone={
                    summary.budget_denied_call_count > 0 ? 'warning' : 'neutral'
                }
            />
        </div>
    );
}

function SummaryMetric({
    label,
    value,
    icon,
    tone = 'neutral',
}: {
    readonly label: string;
    readonly value: number;
    readonly icon: ReactNode;
    readonly tone?: 'neutral' | 'success' | 'warning' | 'danger';
}) {
    return (
        <div className="rounded-[8px] border border-[#eaeaea] bg-white p-4 shadow-[0_1px_1px_rgba(0,0,0,0.03)]">
            <div
                className={cn(
                    'mb-3 flex size-8 items-center justify-center rounded-[6px] border',
                    tone === 'success' &&
                        'border-emerald-200 bg-emerald-50 text-emerald-700',
                    tone === 'warning' &&
                        'border-amber-200 bg-amber-50 text-amber-800',
                    tone === 'danger' &&
                        'border-red-200 bg-red-50 text-red-700',
                    tone === 'neutral' &&
                        'border-[#eaeaea] bg-[#fafafa] text-[#666666]',
                )}
            >
                {icon}
            </div>
            <div className="text-2xl leading-8 font-semibold tracking-normal text-[#171717] tabular-nums">
                {formatNumber(value)}
            </div>
            <div className="mt-1 text-xs font-medium text-[#666666]">
                {label}
            </div>
        </div>
    );
}

function OutputVolumePanel({
    rows,
}: {
    readonly rows: ReadonlyArray<OutputVolumeRow>;
}) {
    return (
        <Panel
            title="Output Volume"
            icon={<BarChart3 className="size-4" />}
            action={`${rows.length} tools`}
        >
            <div className="grid gap-2 sm:hidden">
                {rows.map((row) => (
                    <div
                        key={row.tool_name}
                        className="rounded-[6px] border border-[#ededed] bg-white p-3"
                    >
                        <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
                            <ToolName value={row.tool_name} />
                            <div className="shrink-0 text-right text-xs text-[#666666] tabular-nums">
                                {formatNumber(row.call_count)} calls
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-xs">
                            <MobileMetric
                                label="Visible"
                                value={formatBytes(
                                    row.total_visible_result_size_bytes,
                                )}
                            />
                            <MobileMetric
                                label="Rows"
                                value={formatNumber(row.total_result_count)}
                            />
                            <MobileMetric
                                label="Saved"
                                value={formatBytes(
                                    row.total_compacted_result_bytes_saved,
                                )}
                            />
                        </div>
                        {row.zero_digest_savings ? (
                            <Badge
                                variant="outline"
                                className="mt-3 border-amber-200 bg-amber-50 text-[11px] text-amber-900"
                            >
                                Zero digest savings
                            </Badge>
                        ) : null}
                    </div>
                ))}
                {rows.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[#8f8f8f]">
                        No tool calls
                    </div>
                ) : null}
            </div>
            <Table
                className="min-w-[760px]"
                containerClassName="hidden max-h-[26rem] overflow-auto sm:block"
            >
                <TableHeader className="sticky top-0 z-10 bg-white">
                    <TableRow>
                        <TableHead>Tool</TableHead>
                        <TableHead className="text-right">Calls</TableHead>
                        <TableHead className="text-right">Visible</TableHead>
                        <TableHead className="text-right">Rows</TableHead>
                        <TableHead className="text-right">Saved</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.tool_name} className="align-top">
                            <TableCell>
                                <ToolName value={row.tool_name} />
                                {row.zero_digest_savings ? (
                                    <Badge
                                        variant="outline"
                                        className="mt-1 border-amber-200 bg-amber-50 text-[11px] text-amber-900"
                                    >
                                        Zero digest savings
                                    </Badge>
                                ) : null}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatNumber(row.call_count)}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                <div>
                                    {formatBytes(
                                        row.total_visible_result_size_bytes,
                                    )}
                                </div>
                                <div className="text-xs text-[#8f8f8f]">
                                    avg{' '}
                                    {formatBytes(
                                        row.average_visible_result_size_bytes,
                                    )}
                                </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                <div>
                                    {formatNumber(row.total_result_count)}
                                </div>
                                <div className="text-xs text-[#8f8f8f]">
                                    max {formatNumber(row.max_result_count)}
                                </div>
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                                {formatBytes(
                                    row.total_compacted_result_bytes_saved,
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                    {rows.length === 0 ? (
                        <EmptyRow colSpan={5} label="No tool calls" />
                    ) : null}
                </TableBody>
            </Table>
        </Panel>
    );
}

function FailureTaxonomyPanel({
    rows,
}: {
    readonly rows: ReadonlyArray<FailureTaxonomyRow>;
}) {
    return (
        <Panel
            title="Failure Taxonomy"
            icon={<ShieldAlert className="size-4" />}
            action={`${rows.filter((row) => row.failed_call_count + row.denied_call_count > 0).length} failing tools`}
        >
            <div className="grid gap-2 sm:hidden">
                {rows.map((row) => {
                    const budgetCount = row.category_counts['budget'] ?? 0;
                    const inputValidationCount =
                        row.category_counts['input_validation'] ?? 0;
                    const semanticContractCount =
                        row.category_counts['semantic_tool_contract'] ?? 0;

                    return (
                        <div
                            key={row.tool_name}
                            className="rounded-[6px] border border-[#ededed] bg-white p-3"
                        >
                            <div className="mb-3 flex min-w-0 items-start justify-between gap-3">
                                <ToolName value={row.tool_name} />
                                <div className="shrink-0 text-right text-sm font-semibold text-[#171717] tabular-nums">
                                    {formatRate(row.failure_rate)}
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <MobileMetric
                                    label="Input"
                                    value={formatNumber(inputValidationCount)}
                                />
                                <MobileMetric
                                    label="Contract"
                                    value={formatNumber(semanticContractCount)}
                                />
                                <MobileMetric
                                    label="Uncategorized"
                                    value={formatNumber(
                                        row.uncategorized_failure_count,
                                    )}
                                    tone={
                                        row.uncategorized_failure_count > 0
                                            ? 'danger'
                                            : 'neutral'
                                    }
                                />
                            </div>
                            {budgetCount > 0 ? (
                                <Badge
                                    variant="outline"
                                    className="mt-3 border-amber-200 bg-amber-50 text-[11px] text-amber-900"
                                >
                                    {budgetCount} budget
                                </Badge>
                            ) : null}
                        </div>
                    );
                })}
                {rows.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[#8f8f8f]">
                        No failure data
                    </div>
                ) : null}
            </div>
            <Table
                className="min-w-[680px]"
                containerClassName="hidden max-h-[26rem] overflow-auto sm:block"
            >
                <TableHeader className="sticky top-0 z-10 bg-white">
                    <TableRow>
                        <TableHead>Tool</TableHead>
                        <TableHead className="text-right">Rate</TableHead>
                        <TableHead className="text-right">Input</TableHead>
                        <TableHead className="text-right">Contract</TableHead>
                        <TableHead className="text-right">
                            Uncategorized
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.map((row) => {
                        const budgetCount = row.category_counts['budget'] ?? 0;
                        const inputValidationCount =
                            row.category_counts['input_validation'] ?? 0;
                        const semanticContractCount =
                            row.category_counts['semantic_tool_contract'] ?? 0;

                        return (
                            <TableRow key={row.tool_name} className="align-top">
                                <TableCell>
                                    <ToolName value={row.tool_name} />
                                    {budgetCount > 0 ? (
                                        <Badge
                                            variant="outline"
                                            className="mt-1 border-amber-200 bg-amber-50 text-[11px] text-amber-900"
                                        >
                                            {budgetCount} budget
                                        </Badge>
                                    ) : null}
                                </TableCell>
                                <TableCell className="text-right font-medium tabular-nums">
                                    {formatRate(row.failure_rate)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatNumber(inputValidationCount)}
                                </TableCell>
                                <TableCell className="text-right tabular-nums">
                                    {formatNumber(semanticContractCount)}
                                </TableCell>
                                <TableCell className="text-right">
                                    <span
                                        className={cn(
                                            'tabular-nums',
                                            row.uncategorized_failure_count >
                                                0 &&
                                                'font-semibold text-red-700',
                                        )}
                                    >
                                        {formatNumber(
                                            row.uncategorized_failure_count,
                                        )}
                                    </span>
                                </TableCell>
                            </TableRow>
                        );
                    })}
                    {rows.length === 0 ? (
                        <EmptyRow colSpan={5} label="No failure data" />
                    ) : null}
                </TableBody>
            </Table>
        </Panel>
    );
}

function DrillDownPanel({
    rows,
}: {
    readonly rows: ReadonlyArray<DrillDownRateRow>;
}) {
    return (
        <Panel
            title="Search To Inspect"
            icon={<Search className="size-4" />}
            action={`${rows.length} pairs`}
        >
            <div className="grid gap-2">
                {rows.map((row) => (
                    <div
                        key={`${row.search_tool_name}:${row.inspect_tool_name}`}
                        className="rounded-[6px] border border-[#ededed] bg-white p-3"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-sm font-medium text-[#171717]">
                                    {row.label}
                                </div>
                                <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-[11px] text-[#666666]">
                                    <ToolName value={row.search_tool_name} />
                                    <span>to</span>
                                    <ToolName value={row.inspect_tool_name} />
                                </div>
                            </div>
                            <div className="text-right">
                                <div className="text-lg font-semibold text-[#171717] tabular-nums">
                                    {formatRate(row.follow_rate)}
                                </div>
                                <div className="text-[11px] text-[#8f8f8f] tabular-nums">
                                    {formatNumber(
                                        row.followed_by_inspect_count,
                                    )}{' '}
                                    /{' '}
                                    {formatNumber(row.successful_search_count)}
                                </div>
                            </div>
                        </div>
                        <ProgressBar value={row.follow_rate} />
                    </div>
                ))}
            </div>
        </Panel>
    );
}

function TrendPanel({ rows }: { readonly rows: ReadonlyArray<TrendRow> }) {
    const maxCalls = Math.max(...rows.map((row) => row.callCount), 1);
    return (
        <Panel
            title="Call Volume Trends"
            icon={<Activity className="size-4" />}
            action={`${rows.length} buckets`}
        >
            <div className="max-h-[28rem] overflow-auto">
                <div className="grid min-w-0 gap-1">
                    {rows.map((row) => (
                        <div
                            key={`${row.bucketStart}:${row.toolName}`}
                            className="grid grid-cols-[62px_minmax(0,1fr)] items-center gap-2 rounded-[6px] px-2 py-2 hover:bg-[#fafafa] sm:grid-cols-[74px_minmax(0,1fr)_150px]"
                        >
                            <div className="text-xs text-[#666666]">
                                {formatDay(row.bucketStart)}
                            </div>
                            <ToolName value={row.toolName} />
                            <div className="col-span-2 flex items-center gap-2 sm:col-span-1">
                                <div className="h-2 min-w-0 flex-1 rounded-full bg-[#ededed]">
                                    <div
                                        className="h-2 rounded-full bg-[#171717]"
                                        style={{
                                            width: `${Math.max(
                                                4,
                                                (row.callCount / maxCalls) *
                                                    100,
                                            )}%`,
                                        }}
                                    />
                                </div>
                                <div className="w-24 shrink-0 text-right text-xs text-[#666666] tabular-nums">
                                    {formatNumber(row.callCount)} calls
                                    {row.budgetDeniedCallCount > 0 ? (
                                        <span className="text-amber-700">
                                            {' '}
                                            /{' '}
                                            {formatNumber(
                                                row.budgetDeniedCallCount,
                                            )}{' '}
                                            budget
                                        </span>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    ))}
                    {rows.length === 0 ? (
                        <div className="py-10 text-center text-sm text-[#8f8f8f]">
                            No trend buckets
                        </div>
                    ) : null}
                </div>
            </div>
        </Panel>
    );
}

function RunExamplesPanel({
    title,
    rows,
    metric,
}: {
    readonly title: string;
    readonly rows: ReadonlyArray<RunExampleRow>;
    readonly metric: 'visible' | 'failure';
}) {
    return (
        <Panel
            title={title}
            icon={<ArrowUpRight className="size-4" />}
            action={`${rows.length} runs`}
        >
            <div className="grid gap-2">
                {rows.map((row) => (
                    <Link
                        key={`${row.run_id}:${row.tool_name}:${row.started_at}`}
                        to="/admin/agent/$runId"
                        params={{ runId: row.run_id }}
                        className="group rounded-[6px] border border-[#ededed] bg-white p-3 transition-colors hover:border-[#c9c9c9] hover:bg-[#fafafa]"
                    >
                        <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                                <ToolName value={row.tool_name} />
                                <div className="mt-1 text-xs text-[#666666]">
                                    {formatDateTime(row.started_at)} /{' '}
                                    {row.mode}
                                </div>
                                {row.detail ? (
                                    <div className="mt-1 line-clamp-2 text-xs text-[#4d4d4d]">
                                        {row.detail}
                                    </div>
                                ) : null}
                            </div>
                            <div className="shrink-0 text-right">
                                <StatusBadge
                                    status={row.status}
                                    category={row.error_category ?? null}
                                />
                                {metric === 'visible' ? (
                                    <div className="mt-2 text-xs text-[#666666] tabular-nums">
                                        {formatBytes(
                                            row.visible_result_size_bytes ?? 0,
                                        )}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </Link>
                ))}
                {rows.length === 0 ? (
                    <div className="py-10 text-center text-sm text-[#8f8f8f]">
                        No linked runs
                    </div>
                ) : null}
            </div>
        </Panel>
    );
}

function Panel({
    title,
    icon,
    action,
    children,
}: {
    readonly title: string;
    readonly icon: ReactNode;
    readonly action?: string;
    readonly children: ReactNode;
}) {
    return (
        <section className="min-h-0 rounded-[8px] border border-[#eaeaea] bg-white shadow-[0_1px_1px_rgba(0,0,0,0.03)]">
            <div className="flex h-12 items-center justify-between gap-3 border-b border-[#ededed] px-4">
                <div className="flex min-w-0 items-center gap-2">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-[6px] border border-[#eaeaea] bg-[#fafafa] text-[#4d4d4d]">
                        {icon}
                    </span>
                    <h2 className="truncate text-sm font-semibold text-[#171717]">
                        {title}
                    </h2>
                </div>
                {action ? (
                    <div className="shrink-0 text-xs font-medium text-[#8f8f8f]">
                        {action}
                    </div>
                ) : null}
            </div>
            <div className="p-3">{children}</div>
        </section>
    );
}

function ToolName({ value }: { readonly value: string }) {
    return (
        <span className="inline-block max-w-full truncate rounded-[5px] bg-[#f5f5f5] px-1.5 py-0.5 font-mono text-[11px] leading-4 text-[#171717]">
            {value}
        </span>
    );
}

function MobileMetric({
    label,
    value,
    tone = 'neutral',
}: {
    readonly label: string;
    readonly value: string;
    readonly tone?: 'neutral' | 'danger';
}) {
    return (
        <div>
            <div className="font-medium text-[#8f8f8f]">{label}</div>
            <div
                className={cn(
                    'mt-1 font-semibold text-[#171717] tabular-nums',
                    tone === 'danger' && 'text-red-700',
                )}
            >
                {value}
            </div>
        </div>
    );
}

function StatusBadge({
    status,
    category,
}: {
    readonly status: RunExampleRow['status'];
    readonly category: string | null;
}) {
    const isSuccess = status === 'succeeded';
    const isBudget = category === 'budget';
    return (
        <Badge
            variant="outline"
            className={cn(
                'text-[11px]',
                isSuccess &&
                    'border-emerald-200 bg-emerald-50 text-emerald-800',
                !isSuccess &&
                    !isBudget &&
                    'border-red-200 bg-red-50 text-red-800',
                isBudget && 'border-amber-200 bg-amber-50 text-amber-900',
            )}
        >
            {category ?? status}
        </Badge>
    );
}

function ProgressBar({ value }: { readonly value: number }) {
    return (
        <div className="mt-3 h-2 rounded-full bg-[#ededed]">
            <div
                className="h-2 rounded-full bg-[#171717]"
                style={{ width: `${Math.min(100, Math.max(3, value * 100))}%` }}
            />
        </div>
    );
}

function EmptyRow({
    colSpan,
    label,
}: {
    readonly colSpan: number;
    readonly label: string;
}) {
    return (
        <TableRow>
            <TableCell
                colSpan={colSpan}
                className="h-28 text-center text-sm text-[#8f8f8f]"
            >
                {label}
            </TableCell>
        </TableRow>
    );
}

function StatePanel({
    label,
    tone = 'neutral',
}: {
    readonly label: string;
    readonly tone?: 'neutral' | 'danger';
}) {
    return (
        <div
            className={cn(
                'mx-auto flex h-72 max-w-2xl items-center justify-center rounded-[8px] border bg-white text-sm',
                tone === 'danger'
                    ? 'border-red-200 text-red-700'
                    : 'border-[#eaeaea] text-[#666666]',
            )}
        >
            {tone === 'danger' ? (
                <AlertTriangle className="mr-2 size-4" />
            ) : null}
            {label}
        </div>
    );
}

export type TrendRow = {
    readonly bucketStart: string;
    readonly toolName: string;
    readonly callCount: number;
    readonly failedCallCount: number;
    readonly budgetDeniedCallCount: number;
};

export function buildTrendRows(
    buckets: ReadonlyArray<TrendBucketRow>,
): Array<TrendRow> {
    return [...buckets]
        .sort((left, right) => {
            const timeDelta =
                new Date(right.bucket_start).getTime() -
                new Date(left.bucket_start).getTime();
            if (timeDelta !== 0) {
                return timeDelta;
            }
            return right.call_count - left.call_count;
        })
        .slice(0, 36)
        .map((bucket) => ({
            bucketStart: bucket.bucket_start,
            toolName: bucket.tool_name,
            callCount: bucket.call_count,
            failedCallCount: bucket.failed_call_count,
            budgetDeniedCallCount: bucket.budget_denied_call_count,
        }));
}

export function formatBytes(value: number): string {
    if (value < 1024) {
        return `${formatNumber(Math.round(value))} B`;
    }
    if (value < 1024 * 1024) {
        return `${(value / 1024).toFixed(1)} KB`;
    }
    return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatRate(value: number): string {
    return RATE_FORMATTER.format(value);
}

function formatNumber(value: number): string {
    return NUMBER_FORMATTER.format(Math.round(value));
}

function formatDay(value: string): string {
    return DAY_FORMATTER.format(new Date(value));
}

function formatDateTime(value: string): string {
    return DATE_TIME_FORMATTER.format(new Date(value));
}
